/**
 * HUB-B.1 — Données ventes pour onglet Centre Activité (wrapper /ventes/historique).
 */
import { useCallback, useEffect, useState } from 'react';

export default function useActiviteVentes(api, filters, { enabled = true, canViewFinancialTotals = false } = {}) {
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totaux, setTotaux] = useState({ ventes: 0, acomptes: 0, retraits: 0, global: 0 });
  const [commissions, setCommissions] = useState(null);
  const [error, setError] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const response = await api.get('/payments/sumup/pending');
      setPendingPayments(response.data || []);
    } catch {
      setPendingPayments([]);
    }
  }, [api]);

  const fetchCommissions = useCallback(async () => {
    if (!canViewFinancialTotals) {
      setCommissions(null);
      return;
    }
    if (!filters.vendeur || !filters.date_debut || !filters.date_fin) {
      setCommissions(null);
      return;
    }
    try {
      const response = await api.get('/ventes/commissions', {
        params: {
          vendeur: filters.vendeur,
          date_debut: filters.date_debut,
          date_fin: filters.date_fin,
        },
      });
      setCommissions(response.data);
    } catch {
      setCommissions(null);
    }
  }, [api, filters.vendeur, filters.date_debut, filters.date_fin, canViewFinancialTotals]);

  const fetchVentes = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(currentPage), limit: '20' });
      if (filters.vendeur) params.append('vendeur', filters.vendeur);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      if (filters.search) params.append('search', filters.search);
      if (filters.magasin_id) params.append('magasin_id', filters.magasin_id);

      const response = await api.get(`/ventes/historique?${params.toString()}`);
      setVentes(response.data.ventes || []);
      setTotalPages(response.data.total_pages || 1);
      if (canViewFinancialTotals && response.data.totaux) {
        setTotaux(response.data.totaux);
      } else {
        setTotaux({ ventes: 0, acomptes: 0, retraits: 0, global: 0 });
      }
      await fetchCommissions();
      await fetchPendingPayments();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement ventes');
      setVentes([]);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, currentPage, filters, fetchCommissions, fetchPendingPayments, canViewFinancialTotals]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.vendeur, filters.date_debut, filters.date_fin, filters.search, filters.magasin_id]);

  useEffect(() => {
    fetchVentes();
  }, [fetchVentes]);

  return {
    ventes,
    loading,
    error,
    currentPage,
    totalPages,
    totaux,
    commissions,
    pendingPayments,
    fetchPendingPayments,
    setCurrentPage,
    refresh: fetchVentes,
  };
}
